export const meta: BlockMeta = {
  title: "Info Slide",
  description: "Single Informative slide",
  keywords: ["Info Slider"],
  // @ts-ignore
  icon: "images-alt2",
  cache: false,
  tags: ["info-slider"],
  conditions: {
    multiple: true,
  },
}

import { ClampText } from "@components/atoms/ClampText"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { cn, theme, ThemeTypes, tv, VariantProps } from "@utils/tw"
import { defineBlock, EditableText } from "eddev/blocks"
import { motion, useInView } from "motion/react"
import { ComponentProps, forwardRef, useEffect, useRef, useState } from "react"

type SlideProps = ComponentProps<"div"> & VariantProps<typeof slideStyles> & {}
type SlideWrapperProps = ComponentProps<"div"> & VariantProps<typeof slideWrapperStyles> & {}

export const Slide = forwardRef<HTMLDivElement, SlideProps>(({ className, ...props }, ref) => {
  const styles = slideStyles({ theme: props.theme, className })

  return (
    <div className={cn(styles)} ref={ref} {...props}>
      {props.children}
    </div>
  )
})

export const SlideWrapper = forwardRef<HTMLDivElement, SlideWrapperProps>(({ className, admin, ...props }, ref) => {
  const styles = slideWrapperStyles({ admin: env.admin, className })

  return (
    <div className={cn(styles)} ref={ref} {...props}>
      {props.children}
    </div>
  )
})

export default defineBlock("content/info-slide", (props) => {
  const adminImageAttrs = env.admin ? { aspect: 1 } : { max: true }
  const adminImageWrapperClasses = env.admin ? "aspect-[284/228]" : "aspect-[284/228] md:aspect-square"

  const ref = useRef<HTMLDivElement>(null)
  const [isActive, setIsActive] = useState(env.admin === true ? true : false)

  const inView = useInView(ref, { amount: 0.3, once: false })

  useEffect(() => {
    const el = ref.current?.closest(".swiper-slide")
    if (!el) return

    // Initial check
    setIsActive(env.admin === true ? true : el.classList.contains("swiper-slide-active"))

    const observer = new MutationObserver(() => {
      setIsActive(env.admin === true ? true : el.classList.contains("swiper-slide-active"))
    })

    observer.observe(el, {
      attributes: true,
      attributeFilter: ["class"],
    })

    return () => observer.disconnect()
  }, [])

  return (
    <SlideWrapper ref={ref} admin={env.admin} data-active={isActive}>
      <Slide
        theme={props.blockTheme as ThemeTypes}
        className={`${env.admin ? "h-fit" : "h-full"} md:w-grid-inner md:mx-auto`}
      >
        <div className="w-full md:w-1/2 flex md:items-center md:justify-center md:p-4 mb-[10px] md:mb-0">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
            className={`relative h-full w-full rounded-sm overflow-hidden ${adminImageWrapperClasses}`}
          >
            {props.image && props.mediaType === "image" && <ResponsiveImage {...props.image} {...adminImageAttrs} />}
            {props.video && props.mediaType === "video" && (
              <video
                autoPlay
                muted
                loop
                playsInline
                width={600}
                height={544}
                className={`absolute inset-0 w-full h-full max-w-none object-cover object-center bg-warmBlack`}
              >
                <source src={props.video?.mediaItemUrl!} type="video/mp4" />
              </video>
            )}
          </motion.div>
        </div>
        <div className="w-full md:w-1/2 flex flex-col md:items-start md:justify-center py-5 px-4">
          <ClampText
            size="slide"
            className="flex flex-col gap-y-7 md:gap-y-4 items-start md:justify-center h-fit pb-8 md:pb-0"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
            >
              <EditableText
                as="h2"
                store="title"
                defaultValue="Enter a title"
                className="type-title-xs text-current w-fit text-pretty text-left"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
            >
              <EditableText
                as="p"
                store="text"
                defaultValue="Enter some text"
                className="type-body-m font-medium md:type-body-l text-current w-fit text-pretty text-left"
              />
            </motion.div>
          </ClampText>
        </div>
      </Slide>
    </SlideWrapper>
  )
})

export const slideWrapperStyles = tv({
  base: ["relative z-10 h-full"],
  variants: {
    admin: {
      true: ["w-full py-8"],
    },
  },
})

export const slideStyles = tv({
  base: ["w-full flex flex-col md:flex-row relative rounded-md p-3 md:p-0"],
  variants: {
    theme: {
      ...theme,
    },
  },
  defaultVariants: {
    theme: "blue",
  },
})
